import React, { createContext, useContext, useState } from 'react'
import { createStore, useStore } from 'zustand'
import type { StoreApi } from 'zustand'
import type { SpeechSynthesisEventProps } from '@davi-ai/web-speech-cognitive-services-davi'
import { useUtilsStore } from './UtilsContext'

import { RetorikEvent, RecognitionState, DeviceType, ListStatus, PermissionStatus, Mode } from '../../models/enums'
import getMicrophonePermission from '../../utils/getMicrophonePermission'
import type { CustomVoice, PonyfillFactoryCredentials, SpeechRecognitionOptions } from '../../models/types'
import type { Boundary } from '../../models/speechTypes'
import type { RetorikActivity } from '../../models/activityTypes'

interface SpeechState {
  ponyfillCredentials: PonyfillFactoryCredentials | undefined
  useContinuousRecognition: boolean
  customVoice: CustomVoice | undefined
  voice: SpeechSynthesisVoice | null
  mode: Mode
  disableSound: boolean
  speaking: boolean
  muted: boolean
  cancel: boolean
  boundaryData: Array<Boundary>
  // Viseme data
  visemeData: Array<SpeechSynthesisEventProps>
  singleVisemeAdded: SpeechSynthesisEventProps | null
  multiVisemesAdded: Array<SpeechSynthesisEventProps>
  // Activity data
  currentOrLastPlayedActivity: RetorikActivity | undefined
  // Speech recognition data
  isMicrophoneAllowed: boolean
  showMicrophoneModal: boolean
  permissionStatus: PermissionStatus
  activeRecognitionState: RecognitionState
  lastRecognitionInterim: string
  // Streaming by multiple events data
  streamingReplyToId: string | null
  streamingQueue: Array<RetorikActivity>
  streamingReplyToIdToIgnore: string | null
  // Speech recognition improvement words retrieved through activities
  speechRecognitionDynamicGrammars: Array<string>
  externalTextToSpeech: string | undefined
  actions: {
    setPonyfillFactoryCredentials: (value: PonyfillFactoryCredentials | undefined) => void
    setUseContinuousRecognition: (deviceType: DeviceType, options?: SpeechRecognitionOptions) => void
    setCustomVoice: (value: CustomVoice | undefined) => void
    setVoice: (value: SpeechSynthesisVoice | null) => void
    setMode: (value: Mode) => void
    setDisableSound: (value: boolean) => void
    setSpeaking: (value: boolean) => void
    setMuted: (value: boolean) => void
    toggleMuted: () => void
    cancelCurrentUtterance: () => void
    resetCancelCurrentUtterance: () => void
    setBoundaryData: (value: Array<Boundary>) => void
    setVisemeData: (value: Array<SpeechSynthesisEventProps>) => void
    addViseme: (value: SpeechSynthesisEventProps) => void
    setCurrentOrLastPlayedActivity: (value: RetorikActivity | undefined) => void
    setIsMicrophoneAllowed: (value: boolean) => void
    setShowMicrophoneModal: (value: boolean) => void
    toggleMicrophone: (fromExpectedInput?: boolean) => void
    askPermission: () => Promise<void>
    setActiveRecognitionState: (value: RecognitionState) => void
    setLastRecognitionInterim: (value: string) => void
    setStreamingReplyToId: (value: string | null) => void
    addToStreamingQueue: (value: RetorikActivity) => void
    removeFirstFromStreamingQueue: (twice?: boolean) => void
    killCurrentStreaming: () => void
    checkActivityReplyToId: (replyToId?: string) => void
    addSpeechRecognitionDynamicGrammar: (value: string | Array<string>) => void
    playExternalSynthesis: (value: string) => void
    resetSpeechStore: () => void
  }
}

const initialState = {
  ponyfillCredentials: undefined,
  useContinuousRecognition: false,
  customVoice: undefined,
  voice: null,
  mode: Mode.text,
  disableSound: false,
  speaking: false,
  muted: false,
  cancel: false,
  boundaryData: [],
  visemeData: [],
  singleVisemeAdded: null,
  multiVisemesAdded: [],
  currentOrLastPlayedActivity: undefined,
  isMicrophoneAllowed: false,
  showMicrophoneModal: false,
  permissionStatus: PermissionStatus.waiting,
  activeRecognitionState: RecognitionState.Closed,
  lastRecognitionInterim: '',
  streamingReplyToId: null,
  streamingQueue: [],
  streamingReplyToIdToIgnore: null,
  speechRecognitionDynamicGrammars: [],
  externalTextToSpeech: undefined
}

const createSpeechStore = (
  setRetorikEvent: (value: RetorikEvent) => void,
  getListStatus: () => ListStatus,
  setListStatus: (value: ListStatus) => void,
  setLastListActivity: (value: string | undefined) => void,
  setUserTyping: (value: boolean) => void
) => {
  return createStore<SpeechState>((set, get) => {
    return {
      ...initialState,
      actions: {
        setPonyfillFactoryCredentials: (value: PonyfillFactoryCredentials | undefined): void => {
          set({ ponyfillCredentials: value })
        },
        setUseContinuousRecognition: (deviceType: DeviceType, options?: SpeechRecognitionOptions): void => {
          if (options?.enableContinuousRecognitionOnAllDevices) {
            set({ useContinuousRecognition: true })
          } else if (options?.enableContinuousRecognitionOn) {
            switch (deviceType) {
              case DeviceType.mobile:
                set({
                  useContinuousRecognition: !!options.enableContinuousRecognitionOn.mobile
                })
                break
              case DeviceType.widget:
              case DeviceType.widgetLandscape:
              case DeviceType.widgetBorne:
                set({
                  useContinuousRecognition: !!options.enableContinuousRecognitionOn.widget
                })
                break
              case DeviceType.landscape:
                set({
                  useContinuousRecognition: !!options.enableContinuousRecognitionOn.desktop
                })
                break
              case DeviceType.borne:
                set({
                  useContinuousRecognition: !!options.enableContinuousRecognitionOn.borne
                })
                break
              default:
                set({ useContinuousRecognition: false })
                break
            }
          } else {
            set({ useContinuousRecognition: false })
          }
        },
        setCustomVoice: (value: CustomVoice | undefined): void => {
          set({ customVoice: value })
        },
        setVoice: (value: SpeechSynthesisVoice | null): void => {
          set({ voice: value })
        },
        setMode: (value: Mode): void => {
          set({ mode: value })
          setLastListActivity(undefined)
          value !== Mode.text && setUserTyping(false)
          !get().disableSound && get().actions.setMuted(value === Mode.text)
        },
        setDisableSound: (value: boolean): void => {
          set({ disableSound: value })
        },
        setSpeaking: (value: boolean): void => {
          set(() => {
            return value === false ? { speaking: value, visemeData: [], singleVisemeAdded: null, multiVisemesAdded: [] } : { speaking: value }
          })
          setRetorikEvent(value ? RetorikEvent.SpeechStarted : RetorikEvent.SpeechEnded)
        },
        setMuted: (value: boolean): void => {
          set({ muted: value })
        },
        toggleMuted: (): void => {
          set((state) => ({ muted: !state.muted }))
        },
        cancelCurrentUtterance: (): void => {
          set({ cancel: true })
        },
        resetCancelCurrentUtterance: (): void => {
          set({ cancel: false })
        },
        setBoundaryData: (value: Array<Boundary>): void => {
          set({ boundaryData: value })
        },
        setVisemeData: (value: Array<SpeechSynthesisEventProps>): void => {
          set({ visemeData: value, multiVisemesAdded: value })
        },
        addViseme: (value: SpeechSynthesisEventProps): void => {
          set((state) => ({
            visemeData: [...state.visemeData, value],
            singleVisemeAdded: value
          }))
        },
        setCurrentOrLastPlayedActivity: (value: RetorikActivity | undefined): void => {
          set({ currentOrLastPlayedActivity: value })
          if (value?.attachmentLayout?.toLowerCase() === 'davilist') {
            if (getListStatus() !== ListStatus.opened) {
              setListStatus(ListStatus.available)
            }
          }
        },
        setIsMicrophoneAllowed: (value: boolean): void => {
          set({ isMicrophoneAllowed: value })
        },
        setShowMicrophoneModal: (value: boolean): void => {
          set({ showMicrophoneModal: value })
        },
        toggleMicrophone: (fromExpectedInput?: boolean): void => {
          if (get().mode === Mode.text) {
            get().actions.setActiveRecognitionState(RecognitionState.Closed)
          } else {
            switch (get().activeRecognitionState) {
              // Do nothing if we are in a transition state
              case RecognitionState.Initializing:
              case RecognitionState.Closing:
                return
              case RecognitionState.Listening: {
                get().actions.setActiveRecognitionState(RecognitionState.ForceClosing)
                get().actions.cancelCurrentUtterance()
                break
              }
              case RecognitionState.Closed: {
                get().actions.setActiveRecognitionState(RecognitionState.Initializing)
                !fromExpectedInput && get().actions.cancelCurrentUtterance()
                break
              }
            }
          }
        },
        askPermission: async (): Promise<void> => {
          set({ showMicrophoneModal: true })

          getMicrophonePermission().then((permission) => {
            // Hide modal window and open microphone if this one is allowed
            if (permission === PermissionStatus.allowed) {
              set({
                permissionStatus: permission,
                isMicrophoneAllowed: true,
                showMicrophoneModal: false
              })
              get().actions.toggleMicrophone()
            } else {
              set({
                permissionStatus: permission,
                isMicrophoneAllowed: false
              })
            }
          })
        },
        setActiveRecognitionState: (value: RecognitionState): void => {
          set({ activeRecognitionState: value })
        },
        setLastRecognitionInterim: (value: string): void => {
          set({ lastRecognitionInterim: value })
        },
        setStreamingReplyToId: (value: string | null): void => {
          set({
            streamingReplyToId: value
          })
        },
        addToStreamingQueue: (value: RetorikActivity): void => {
          if (value.replyToId) {
            if (value.replyToId !== get().streamingReplyToIdToIgnore) {
              // Set replyToId and new queue if replyToId is different from the current one
              if (get().streamingReplyToId !== value.replyToId) {
                set({
                  streamingReplyToId: value.replyToId,
                  streamingQueue: [value]
                })
              } else {
                set({
                  streamingQueue: [...get().streamingQueue, value]
                })
              }
            }
          } else {
            set({
              streamingQueue: [...get().streamingQueue, value]
            })
          }
        },
        // If "twice" parameter is true, let's remove 2 elements instead of one (used with the message at the end of a streaming that should not be spoken)
        removeFirstFromStreamingQueue: (twice?: boolean): void => {
          set((state) => ({
            streamingQueue: state.streamingQueue.slice(twice ? 2 : 1)
          }))
        },
        killCurrentStreaming: (): void => {
          set({
            streamingReplyToIdToIgnore: get().streamingReplyToId,
            streamingQueue: []
          })
        },
        checkActivityReplyToId: (replyToId?: string): void => {
          get().streamingReplyToId !== replyToId && get().actions.killCurrentStreaming()
        },
        addSpeechRecognitionDynamicGrammar: (value: string | Array<string>): void => {
          const currentGrammars = get().speechRecognitionDynamicGrammars

          // For each value, verifiy if it doesn't exist in the array yet
          if (Array.isArray(value)) {
            const valuesToAdd: Array<string> = []
            value.forEach((val) => {
              !currentGrammars.includes(val) && valuesToAdd.push(val)
            })

            valuesToAdd.length &&
              set({
                speechRecognitionDynamicGrammars: [...currentGrammars, ...valuesToAdd]
              })
          } else {
            !currentGrammars.includes(value) &&
              set({
                speechRecognitionDynamicGrammars: [...currentGrammars, value]
              })
          }
        },
        playExternalSynthesis: (value: string): void => {
          set({ externalTextToSpeech: value })
        },
        resetSpeechStore: (): void => {
          set({ ...initialState })
        }
      }
    }
  })
}

const SpeechContext = createContext<StoreApi<SpeechState> | null>(null)

const useSpeechStore = <T,>(selector: (state: SpeechState) => T): T => {
  const store = useContext(SpeechContext)
  if (!store) throw new Error('Retorik Framework > useSpeechStore must be used within SpeechProvider')
  return useStore(store, selector)
}

const SpeechProvider = ({ children }) => {
  const { setRetorikEvent, getListStatus, setListStatus, setLastListActivity, setUserTyping } = useUtilsStore((state) => state.actions)
  const [store] = useState(() => createSpeechStore(setRetorikEvent, getListStatus, setListStatus, setLastListActivity, setUserTyping))

  return <SpeechContext.Provider value={store}>{children}</SpeechContext.Provider>
}

export default SpeechProvider
export { useSpeechStore }
